import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from 'react';

/**
 * CWY Button — the brand action control (ported from the CWY Design System).
 * Variants: primary (forest), confirm (mint — "Move to Green"/Send),
 * secondary (paper outline), ghost (quiet). Sizes: sm, md.
 *
 * Hover and press live in `.cwy-btn` (globals.css) rather than in React state:
 * the old version re-rendered on every pointer cross and left the hover style
 * stuck after a tap on touch devices.
 */
export type ButtonVariant = 'primary' | 'confirm' | 'secondary' | 'ghost' | 'accent';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: 'sm' | 'md';
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
}

/** Per-variant colours, handed to `.cwy-btn` as custom properties. */
const VARIANT: Record<ButtonVariant, Record<string, string>> = {
  primary: {
    '--btn-bg': 'var(--action-bg)',
    '--btn-bg-hover': 'var(--action-bg-hover)',
    '--btn-fg': 'var(--action-text)',
    '--btn-border': 'transparent',
  },
  confirm: {
    '--btn-bg': 'var(--confirm-bg)',
    '--btn-bg-hover': 'var(--confirm-bg)',
    '--btn-fg': 'var(--confirm-text)',
    '--btn-border': 'var(--confirm-border)',
  },
  secondary: {
    '--btn-bg': 'var(--surface-card)',
    '--btn-bg-hover': 'var(--surface-sunk)',
    '--btn-fg': 'var(--text-body)',
    '--btn-border': 'var(--border-strong)',
  },
  ghost: {
    '--btn-bg': 'transparent',
    '--btn-bg-hover': 'var(--surface-sunk)',
    '--btn-fg': 'var(--text-body)',
    '--btn-border': 'transparent',
  },
  // Lime pill CTA for dark (forest) surfaces — the "Open scaffold" style
  // buttons from the CWY Design System mockup.
  accent: {
    '--btn-bg': 'var(--brand-lime)',
    '--btn-bg-hover': '#AFCB86',
    '--btn-fg': 'var(--brand-forest-900)',
    '--btn-border': 'transparent',
  },
};

export function Button({
  children,
  variant = 'primary',
  size = 'md',
  disabled = false,
  iconLeft = null,
  iconRight = null,
  type = 'button',
  className,
  style,
  ...rest
}: ButtonProps) {
  // Height and text come from the same --control-* tokens the form primitives
  // use. Previously these were fixed px (13px text in ~29px of chrome) while
  // inputs were rem-scaled to ~43px, so a button never lined up with the field
  // it sat beside.
  const metrics =
    size === 'sm'
      ? {
          height: 'var(--control-h-sm)',
          padding: '0 var(--control-px-sm)',
          fontSize: 'var(--control-text-sm)',
          gap: '6px',
        }
      : {
          height: 'var(--control-h-md)',
          padding: '0 var(--control-px-md)',
          fontSize: 'var(--control-text-md)',
          gap: '7px',
        };

  return (
    <button
      type={type}
      disabled={disabled}
      className={className ? `cwy-btn ${className}` : 'cwy-btn'}
      style={{ ...metrics, ...VARIANT[variant], ...style } as CSSProperties}
      {...rest}
    >
      {iconLeft}
      {children}
      {iconRight}
    </button>
  );
}
